import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  Home,
  BookOpen,
  Calendar,
  GraduationCap,
  Activity,
  Users,
  Settings,
  BarChart3,
  FileText,
  UserCog,
  School,
  Clock,
  Award,
  Database,
  UserPlus,
  ChevronLeft,
  ChevronRight,
  X,
  TrendingUp,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useTranslation } from '../../contexts/LanguageContext';
import { Environment } from '../../utils/environment';
import { getPortalTitle } from '../../constants';
import type { UserRole } from '../../types';

interface NavItem {
  icon: React.ReactNode;
  labelKey: string;
  href: string;
  roles: UserRole[];
  isEnabled: boolean;
}

const navigationItems: NavItem[] = [
  // Student Navigation
  { icon: <BookOpen className="h-5 w-5" />, labelKey: 'sidebar.krsManagement', href: '/student/krs', roles: ['student'], isEnabled: Environment.pages.studentKRS },
  { icon: <Calendar className="h-5 w-5" />, labelKey: 'sidebar.classSchedule', href: '/student/schedule', roles: ['student'], isEnabled: Environment.pages.studentSchedule },
  { icon: <GraduationCap className="h-5 w-5" />, labelKey: 'sidebar.grades', href: '/student/grades', roles: ['student'], isEnabled: Environment.pages.studentGrades },
  { icon: <Clock className="h-5 w-5" />, labelKey: 'sidebar.attendance', href: '/student/attendance', roles: ['student'], isEnabled: Environment.pages.studentAttendance },
  { icon: <FileText className="h-5 w-5" />, labelKey: 'sidebar.transcript', href: '/student/transcript', roles: ['student'], isEnabled: Environment.pages.studentTranscript },
  { icon: <Activity className="h-5 w-5" />, labelKey: 'sidebar.mbkmActivities', href: '/student/activities', roles: ['student'], isEnabled: Environment.pages.studentActivities },
  
  // Lecturer Navigation
  { icon: <Calendar className="h-5 w-5" />, labelKey: 'sidebar.classSchedule', href: '/lecturer/schedule', roles: ['lecturer'], isEnabled: Environment.pages.lecturerSchedule },
  { icon: <Award className="h-5 w-5" />, labelKey: 'sidebar.gradeInput', href: '/lecturer/grade-input', roles: ['lecturer'], isEnabled: Environment.pages.lecturerGradeInput },
  { icon: <BarChart3 className="h-5 w-5" />, labelKey: 'sidebar.studentPerformance', href: '/lecturer/performance', roles: ['lecturer'], isEnabled: Environment.pages.lecturerPerformance },
  { icon: <UserCog className="h-5 w-5" />, labelKey: 'sidebar.academicAdvisor', href: '/lecturer/advisor', roles: ['lecturer'], isEnabled: Environment.pages.lecturerAdvisor },
  
  // Admin Navigation
  { icon: <Users className="h-5 w-5" />, labelKey: 'sidebar.classManagement', href: '/admin/classes', roles: ['admin'], isEnabled: Environment.pages.adminClasses },
  { icon: <Database className="h-5 w-5" />, labelKey: 'sidebar.masterData', href: '/admin/master-data', roles: ['admin'], isEnabled: Environment.pages.adminMasterData },
  { icon: <School className="h-5 w-5" />, labelKey: 'sidebar.programs', href: '/admin/programs', roles: ['admin'], isEnabled: Environment.pages.adminPrograms },
  { icon: <BookOpen className="h-5 w-5" />, labelKey: 'sidebar.curriculum', href: '/admin/curriculum', roles: ['admin'], isEnabled: Environment.pages.adminCurriculum },
  { icon: <Users className="h-5 w-5" />, labelKey: 'sidebar.userManagement', href: '/admin/users', roles: ['admin'], isEnabled: Environment.pages.adminUsers },
  { icon: <UserPlus className="h-5 w-5" />, labelKey: 'sidebar.roleRequests', href: '/admin/role-requests', roles: ['admin'], isEnabled: Environment.pages.adminRoleRequests },
  { icon: <Clock className="h-5 w-5" />, labelKey: 'sidebar.semesterSetup', href: '/admin/semester', roles: ['admin'], isEnabled: Environment.pages.adminSemester },
  { icon: <Calendar className="h-5 w-5" />, labelKey: 'sidebar.classScheduling', href: '/admin/scheduling', roles: ['admin'], isEnabled: Environment.pages.adminScheduling },
  { icon: <FileText className="h-5 w-5" />, labelKey: 'sidebar.reports', href: '/admin/reports', roles: ['admin'], isEnabled: Environment.pages.adminReports },
  { icon: <TrendingUp className="h-5 w-5" />, labelKey: 'sidebar.attendanceSummary', href: '/admin/attendance-summary', roles: ['admin'], isEnabled: Environment.pages.adminAttendanceSummary },

  // Guest Navigation
  { icon: <UserPlus className="h-5 w-5" />, labelKey: 'sidebar.roleManagement', href: '/guest/role-management', roles: ['guest'], isEnabled: Environment.pages.guestRoleManagement },
  
  // Role-specific Profile/Settings (Academic/Professional configurations)
  { icon: <UserCog className="h-5 w-5" />, labelKey: 'sidebar.academicProfile', href: '/student/profile', roles: ['student'], isEnabled: Environment.pages.studentProfile },
  { icon: <Settings className="h-5 w-5" />, labelKey: 'sidebar.academicSettings', href: '/student/settings', roles: ['student'], isEnabled: Environment.pages.studentSettings },
  { icon: <UserCog className="h-5 w-5" />, labelKey: 'sidebar.lecturerProfile', href: '/lecturer/profile', roles: ['lecturer'], isEnabled: Environment.pages.lecturerProfile },
  { icon: <Settings className="h-5 w-5" />, labelKey: 'sidebar.lecturerSettings', href: '/lecturer/settings', roles: ['lecturer'], isEnabled: Environment.pages.lecturerSettings },
  { icon: <UserCog className="h-5 w-5" />, labelKey: 'sidebar.adminProfile', href: '/admin/profile', roles: ['admin'], isEnabled: Environment.pages.adminProfile },
  { icon: <Settings className="h-5 w-5" />, labelKey: 'sidebar.adminSettings', href: '/admin/settings', roles: ['admin'], isEnabled: Environment.pages.adminSettings },
  { icon: <UserCog className="h-5 w-5" />, labelKey: 'sidebar.guestProfile', href: '/guest/profile', roles: ['guest'], isEnabled: Environment.pages.guestProfile },
];

const Sidebar: React.FC = () => {
  const { user } = useAuth();
  const { isCollapsed, toggleSidebar, isMobile } = useSidebar();
  const { t } = useTranslation();
  const userRole = user?.role || 'guest';

  // Generate role-first dashboard link
  const dashboardHref = `/${userRole}/dashboard`;

  const filteredItems = navigationItems.filter(item => 
    item.roles.includes(userRole) && item.isEnabled
  );

  const sidebarContent = (
    <>
      {/* Logo/Brand with Toggle Button */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
        {!isCollapsed && (
          <div className="flex items-center space-x-3 flex-1">
            <img 
              src={`${import.meta.env.BASE_URL}logo.png`}
              alt="Institution Logo" 
              className="h-10 w-10 object-contain"
            />
            <div>
              <div className="font-semibold text-gray-900 text-sm">ADB-AS</div>
              <div className="text-xs text-gray-500">{getPortalTitle(userRole)}</div>
            </div>
          </div>
        )}
        {!isMobile && (
          <button
            onClick={toggleSidebar}
            className="flex items-center justify-center p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={isCollapsed ? t('common.expand') : t('common.collapse')}
          >
            {isCollapsed ? (
              <ChevronRight className="h-5 w-5 text-gray-600" />
            ) : (
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            )}
          </button>
        )}
        {isMobile && (
          <button
            onClick={toggleSidebar}
            className="flex items-center justify-center p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
        {/* Dashboard - always first */}
        <NavLink
          to={dashboardHref}
          title={isCollapsed ? t('common.dashboard') : ''}
          onClick={() => isMobile && toggleSidebar()}
          className={({ isActive }) =>
            `flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
            }`
          }
        >
          <Home className="h-5 w-5 flex-shrink-0" />
          {!isCollapsed && <span>{t('common.dashboard')}</span>}
        </NavLink>
        
        {/* Other navigation items */}
        {filteredItems.map((item, index) => (
          <NavLink
            key={index}
            to={item.href}
            title={isCollapsed ? t(item.labelKey) : ''}
            onClick={() => isMobile && toggleSidebar()}
            className={({ isActive }) =>
              `flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-600'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }`
            }
          >
            <span className="flex-shrink-0">{item.icon}</span>
            {!isCollapsed && <span>{t(item.labelKey)}</span>}
          </NavLink>
        ))}
      </nav>
    </>
  );

  // Mobile: Show as overlay when expanded
  if (isMobile) {
    return (
      <>
        {/* Mobile Sidebar - always rendered, slides in/out */}
        <aside
          className={`fixed left-0 top-0 h-full w-64 bg-white border-r border-gray-200 z-50 transition-transform duration-300 ease-in-out lg:hidden ${
            isCollapsed ? '-translate-x-full' : 'translate-x-0'
          }`}
        >
          <div className="flex flex-col h-full pt-16">
            {sidebarContent}
          </div>
        </aside>
      </>
    );
  }

  // Desktop: Show side-by-side
  return (
    <aside className={`fixed left-0 top-0 h-full bg-white border-r border-gray-200 z-50 transition-all duration-300 ease-in-out hidden lg:flex lg:flex-col ${
      isCollapsed ? 'w-20' : 'w-64'
    }`}>
      <div className="flex flex-col h-full">
        {sidebarContent}
      </div>
    </aside>
  );
};

export default Sidebar;